// Client-facing Support Tickets section — list + create + detail.
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, LifeBuoy, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { ticketsApi, companiesApi } from '../../services/api';
import { getApiError } from '../../utils/errorHelper';
import { TicketDetailView, StatusBadge } from '../tickets/TicketDetailView';

function formatTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleString('en-IN', {
    day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  });
}

export const TicketsSection = ({ onNavigateToCompany, onNavigateToOrder } = {}) => {
  const [tickets, setTickets] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [openTicketId, setOpenTicketId] = useState(null);
  const [form, setForm] = useState({ subject: '', description: '', company_id: '' });
  const [submitting, setSubmitting] = useState(false);

  // Deep-link: ?ticket=<id>
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tId = params.get('ticket');
    if (tId) setOpenTicketId(tId);
  }, []);

  const load = async () => {
    try {
      const [t, c] = await Promise.all([ticketsApi.list(), companiesApi.getAll()]);
      setTickets(t);
      setCompanies(c);
      if (c.length && !form.company_id) setForm(f => ({ ...f, company_id: c[0].id }));
    } catch (e) {
      toast.error(getApiError(e, 'Failed to load tickets'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleCreate = async () => {
    if (!form.company_id) {
      toast.error('Please select the company for this ticket');
      return;
    }
    if (!form.subject.trim() || !form.description.trim()) {
      toast.error('Subject and description are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { subject: form.subject.trim(), description: form.description.trim(), company_id: form.company_id };
      const t = await ticketsApi.create(payload);
      toast.success(`Ticket #${t.ticket_number} created`);
      setShowForm(false);
      setForm({ subject: '', description: '', company_id: form.company_id });
      setOpenTicketId(t.id);
      await load();
    } catch (e) {
      toast.error(getApiError(e, 'Failed to create ticket'));
    } finally {
      setSubmitting(false);
    }
  };

  if (openTicketId) {
    return (
      <TicketDetailView
        ticketId={openTicketId}
        isAdmin={false}
        onBack={() => { setOpenTicketId(null); load(); }}
        onNavigateToCompany={onNavigateToCompany}
        onNavigateToOrder={onNavigateToOrder}
      />
    );
  }

  return (
    <div data-testid="client-tickets-section">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-2">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
            <LifeBuoy className="w-6 h-6 text-emerald-400" /> Communication
          </h2>
          <p className="text-slate-400 text-sm mt-1">Raise a communication for any issue with your compliance, orders, or account.</p>
        </div>
        <Button
          onClick={() => setShowForm(v => !v)}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="client-new-ticket-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> {showForm ? 'Cancel' : 'New Communication'}
        </Button>
      </div>

      {showForm && (
        <div className="mb-6 p-5 bg-[#0f1611] border border-emerald-900/30 rounded-xl" data-testid="client-new-ticket-form">
          <div className="mb-3">
            <label className="block text-xs text-slate-400 mb-1">Select Company <span className="text-red-400">*</span></label>
            <select
              value={form.company_id}
              onChange={e => setForm(f => ({ ...f, company_id: e.target.value }))}
              className="w-full bg-[#0a0f0d] border border-emerald-900/30 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
              data-testid="client-new-ticket-company"
            >
              {companies.length === 0 && <option value="">— No companies —</option>}
              {companies.length > 0 && !form.company_id && <option value="">Select a company…</option>}
              {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
            <p className="text-[10px] text-slate-500 mt-1">This helps our team quickly identify which company your ticket belongs to.</p>
          </div>
          <div className="mb-3">
            <label className="block text-xs text-slate-400 mb-1">Subject</label>
            <input
              type="text"
              value={form.subject}
              onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
              placeholder="Short summary of the issue"
              className="w-full bg-[#0a0f0d] border border-emerald-900/30 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
              data-testid="client-new-ticket-subject"
            />
          </div>
          <div className="mb-3">
            <label className="block text-xs text-slate-400 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Describe your issue in detail…"
              rows={5}
              className="w-full bg-[#0a0f0d] border border-emerald-900/30 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500 resize-none"
              data-testid="client-new-ticket-description"
            />
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleCreate}
              disabled={submitting || !form.company_id || !form.subject.trim() || !form.description.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="client-new-ticket-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
              Create Communication
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
      ) : tickets.length === 0 ? (
        <div className="p-10 text-center bg-[#0f1611] border border-emerald-900/30 rounded-xl" data-testid="client-tickets-empty">
          <LifeBuoy className="w-8 h-8 text-slate-600 mx-auto mb-2" />
          <p className="text-sm text-slate-400">No tickets yet. Click &quot;New Ticket&quot; to raise your first one.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="client-tickets-list">
          {tickets.map(t => (
            <button
              key={t.id}
              onClick={() => setOpenTicketId(t.id)}
              className="w-full text-left p-4 bg-[#0f1611] border border-emerald-900/30 hover:border-emerald-500/40 rounded-xl transition-colors flex items-start justify-between gap-4"
              data-testid={`client-ticket-row-${t.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[11px] text-slate-500 font-mono">{t.conversation_id || `#${t.ticket_number}`}</span>
                  <span className="font-semibold text-white truncate">{t.subject}</span>
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Updated {formatTime(t.last_activity_at)}</span>
                  {t.assigned_admin_name && <span>Assigned: {t.assigned_admin_name}</span>}
                </div>
              </div>
              <StatusBadge status={t.status} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TicketsSection;
